import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import type { LoginResponse } from '../api/auth'
import { fetchDepartments, type Department } from '../api/catalog'
import {
  financeReportsApi,
  type AgentMarginRow, type IncomeStatement, type PracticeFilters, type MarginDimension, type MarginLevels, type MarginLevelsRow, type OperatorReportRow, type PracticeMarginList, type SnapshotDetail, type SnapshotInfo,
} from '../api/financeReports'
import { useFeatureFlag } from '../lib/featureFlags'
import { dateIt, eur, errMsg, monthLabel, num, pct, useOperatorNames } from '../lib/financeFormat'
import {
  BarChart, Button, Callout, Card, CardContent, CardHeader, ChartCard, DataTable, DataTableEmptyRow, DateField, Dialog, EmptyState, FilterBar, KeyValueList, LoadingState,
  Page, PageHeader, Pagination, Select, StatCard, StatGrid, StatusPill, Tabs,
} from '../components/ui'

/**
 * Blocco 1 / Card D (REQ-163-001, 164-001, 165-001, 166-001, 182-001, 200-002, 201-001, 202-001) — conto economico
 * gestionale e report di marginalita': schede P&L, Operatori, Pratiche, Agenti e Snapshot mensili. Nessun calcolo in
 * pagina: tutte le cifre arrivano dal server, che le nasconde (chiave assente) a chi non ha `CanSeeRealAmounts`; qui
 * appaiono come "—". Ogni scheda e' filtrata dal proprio flag. Il margine agenti e' solo Admin (403 per gli altri).
 */

interface Props {
  auth: LoginResponse
}

type Tab = 'pnl' | 'levels' | 'operators' | 'practices' | 'agents' | 'snapshots'

function monthStart(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

interface Range {
  from: string
  to: string
  departmentId: string
}

export function IncomeStatementPage({ auth }: Props) {
  const isAdmin = auth.role === 'Administrator' || auth.role === 'EqualAdmin'
  const pnlOn = useFeatureFlag('finanza.conto_economico')
  const marginOn = useFeatureFlag('finanza.report_marginalita')
  const snapshotsOn = useFeatureFlag('finanza.snapshot_mensili')
  const levelsOn = useFeatureFlag('finanza.margini_livelli')
  // R3-17: il drill-down dalle righe dei report arriva con ?tab=practices&from&to&operatorId|agentUserId|departmentId|productId|noDepartment.
  const [params, setParams] = useSearchParams()
  const [tab, setTab] = useState<Tab>(params.get('tab') === 'practices' ? 'practices' : 'pnl')
  const [form, setForm] = useState<Range>({ from: params.get('from') ?? monthStart(), to: params.get('to') ?? today(), departmentId: '' })
  const [range, setRange] = useState<Range>(form)
  const practiceFilters = useMemo<PracticeFilters>(() => ({
    operatorId: params.get('operatorId') ?? undefined, agentUserId: params.get('agentUserId') ?? undefined,
    departmentId: params.get('departmentId') ?? undefined, productId: params.get('productId') ?? undefined,
    noDepartment: params.get('noDepartment') === 'true' || undefined,
  }), [params])
  const [departments, setDepartments] = useState<Department[]>([])

  useEffect(() => {
    let alive = true
    void fetchDepartments().then(d => { if (alive) setDepartments(d) }).catch(() => undefined)
    return () => { alive = false }
  }, [])

  const tabs = useMemo(() => [
    ...(pnlOn ? [{ id: 'pnl', label: 'Conto economico' }] : []),
    ...(levelsOn ? [{ id: 'levels', label: 'Margini per livello' }] : []),
    ...(marginOn ? [{ id: 'operators', label: 'Operatori' }, { id: 'practices', label: 'Pratiche' }, ...(isAdmin ? [{ id: 'agents', label: 'Agenti' }] : [])] : []),
    ...(snapshotsOn ? [{ id: 'snapshots', label: 'Snapshot mensili' }] : []),
  ], [pnlOn, marginOn, snapshotsOn, levelsOn, isAdmin])

  // Un drill-down in arrivo apre l'elenco pratiche anche se la pagina e' gia' montata.
  useEffect(() => {
    if (params.get('tab') !== 'practices') return
    setTab('practices')
    const r = { from: params.get('from') ?? monthStart(), to: params.get('to') ?? today(), departmentId: '' }
    setForm(r)
    setRange(r)
  }, [params])

  const active = tabs.some(t => t.id === tab) ? tab : (tabs[0]?.id as Tab | undefined)
  const usesRange = active !== undefined && active !== 'snapshots'

  return (
    <Page>
      <PageHeader title="Conto economico" icon="query_stats" />
      {tabs.length === 0 || active === undefined ? (
        <EmptyState icon="toggle_off" title="Funzione non attiva" description="Il conto economico e i report di marginalità non sono ancora attivi per il tuo utente." />
      ) : (
        <>
          <Tabs aria-label="Sezioni del conto economico" value={active} onChange={id => setTab(id as Tab)} items={tabs} />
          {usesRange && (
            <FilterBar onSubmit={() => setRange(form)} onReset={() => { const r = { from: monthStart(), to: today(), departmentId: '' }; setForm(r); setRange(r) }}>
              <DateField kind="date" label="Dal" value={form.from} onChange={e => setForm({ ...form, from: e.target.value })} />
              <DateField kind="date" label="Al" value={form.to} onChange={e => setForm({ ...form, to: e.target.value })} />
              {active === 'pnl' && (
                <Select label="Settore" value={form.departmentId} onChange={e => setForm({ ...form, departmentId: e.target.value })}>
                  <option value="">Tutti</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </Select>
              )}
            </FilterBar>
          )}
          {active === 'pnl' && <PnlTab range={range} departments={departments} />}
          {active === 'levels' && <LevelsTab range={range} isAdmin={isAdmin} />}
          {active === 'operators' && <OperatorsTab range={range} />}
          {active === 'practices' && <PracticesTab range={range} filters={practiceFilters} departments={departments} onClearFilters={() => setParams({})} />}
          {active === 'agents' && <AgentsTab range={range} />}
          {active === 'snapshots' && <SnapshotsTab />}
        </>
      )}
    </Page>
  )
}

/** Carica un report quando cambia il periodo; gli errori restano in pagina (mai un 404 nudo). */
function useReport<T>(fetcher: () => Promise<T>, deps: unknown[], failure: string) {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(fetcher, deps)
  useEffect(() => {
    let alive = true
    setLoading(true)
    setError('')
    run().then(r => { if (alive) setData(r) }).catch(e => { if (alive) { setData(null); setError(errMsg(e, failure)) } }).finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [run, failure])
  return { data, loading, error }
}

function Section({ loading, error, children, hasData }: { loading: boolean; error: string; hasData: boolean; children: ReactNode }) {
  if (error) return <Callout tone="error">{error}</Callout>
  if (loading && !hasData) return <LoadingState />
  return <>{children}</>
}

/* ── Conto economico ─────────────────────────────────────────────────────────────────────────── */

function PnlTab({ range, departments }: { range: Range; departments: Department[] }) {
  const { data, loading, error } = useReport<IncomeStatement>(
    () => financeReportsApi.incomeStatement({ from: range.from, to: range.to, departmentId: range.departmentId }),
    [range.from, range.to, range.departmentId], 'Impossibile caricare il conto economico.',
  )
  const deptName = (id: number | null, name?: string) => name ?? (id === null ? 'Senza settore' : departments.find(d => d.id === id)?.name ?? `Settore ${id}`)
  const direct = data?.directCosts
  const hidden = data !== null && data.revenues === undefined

  return (
    <Section loading={loading} error={error} hasData={data !== null}>
      {hidden && <Callout tone="info">Gli importi non sono visibili con il tuo profilo.</Callout>}
      {data?.sharedCostsIncluded && (
        <Callout tone="info">Il margine del settore comprende {eur(data.sharedCosts)} di costi comuni ripartiti dalle regole di ripartizione.</Callout>
      )}
      <StatGrid>
        <StatCard label="Ricavi" value={eur(data?.revenues?.total)} />
        <StatCard label="Margine di contribuzione" value={eur(data?.contributionMargin)} />
        <StatCard label="EBITDA" value={eur(data?.ebitda)} tone={data?.ebitda !== undefined && data.ebitda < 0 ? 'error' : 'neutral'} />
        <StatCard label="Margine" value={pct(data?.marginPct)} />
      </StatGrid>

      <ChartCard title="Ricavi per settore" subtitle={`${dateIt(range.from)} – ${dateIt(range.to)}`}>
        <BarChart
          label="Ricavi per settore"
          categoryHeading="Settore"
          horizontal
          unit="eur"
          loading={loading}
          categories={(data?.revenues?.byDepartment ?? []).map(r => deptName(r.departmentId, r.name))}
          series={[{ id: 'ricavi', name: 'Ricavi', color: 'primary', values: (data?.revenues?.byDepartment ?? []).map(r => r.amount) }]}
          emptyTitle="Nessun ricavo nel periodo"
        />
      </ChartCard>

      <DataTable aria-label="Ricavi per settore: pratiche" caption="Ricavi per settore">
        <thead><tr><th>Settore</th><th className="text-end">Ricavi</th><th className="text-end">Provvigioni</th><th className="text-end">Margine</th><th><span className="md-sr-only">Pratiche</span></th></tr></thead>
        <tbody>
          {(data?.revenues?.byDepartment ?? []).map(r => (
            <tr key={String(r.departmentId)}>
              <td>{deptName(r.departmentId, r.name)}</td>
              <td className="text-end md-num">{eur(r.amount)}</td>
              <td className="text-end md-num">{eur(r.commissions)}</td>
              <td className="text-end md-num">{eur(r.margin)}</td>
              <td>{r.drill && <Link to={r.drill.href}>Pratiche</Link>}</td>
            </tr>
          ))}
        </tbody>
      </DataTable>

      <DataTable aria-label="Conto economico gestionale" caption="Conto economico gestionale">
        <thead><tr><th>Voce</th><th className="text-end">Importo</th></tr></thead>
        <tbody>
          <tr><th scope="row">Ricavi</th><td className="text-end md-num">{eur(data?.revenues?.total)}</td></tr>
          <tr><td>Costi diretti: provvigioni</td><td className="text-end md-num">{eur(direct?.commissions)}</td></tr>
          <tr><td>Costi diretti: lead</td><td className="text-end md-num">{eur(direct?.lead)}</td></tr>
          <tr><td>Costi diretti: variabili</td><td className="text-end md-num">{eur(direct?.variable)}</td></tr>
          <tr><td>Costi diretti: altri</td><td className="text-end md-num">{eur(direct?.other)}</td></tr>
          <tr><th scope="row">Margine di contribuzione</th><td className="text-end md-num">{eur(data?.contributionMargin)}</td></tr>
          {(data?.operatingCosts ?? []).map(c => (
            <tr key={`${c.categoryId}-${c.name}`}><td>Costi operativi: {c.name}</td><td className="text-end md-num">{eur(c.amount)}</td></tr>
          ))}
          <tr><th scope="row">EBITDA</th><td className="text-end md-num">{eur(data?.ebitda)}</td></tr>
        </tbody>
      </DataTable>

      {(data?.bankRevenueByIncomeCategory?.length ?? 0) > 0 && (
        <DataTable aria-label="Incassi bancari per categoria di ricavo" caption="Incassi bancari per categoria di ricavo (lettura per categoria, non si somma ai ricavi sopra)">
          <thead><tr><th>Categoria di ricavo</th><th className="text-end">Movimenti</th><th className="text-end">Incassato</th></tr></thead>
          <tbody>
            {data?.bankRevenueByIncomeCategory?.map(c => (
              <tr key={String(c.categoryId)}>
                <td>{c.name}</td><td className="text-end md-num">{c.movements}</td><td className="text-end md-num">{eur(c.amount)}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
    </Section>
  )
}

/* ── Margini per livello (C-07) ──────────────────────────────────────────────────────────────── */

const DIMENSIONS: { id: MarginDimension; label: string; adminOnly?: boolean }[] = [
  { id: 'prodotto', label: 'Prodotto' }, { id: 'pratica', label: 'Pratica' }, { id: 'cliente', label: 'Cliente' },
  { id: 'operatore', label: 'Operatore' }, { id: 'settore', label: 'Settore' }, { id: 'agente', label: 'Agente', adminOnly: true },
  { id: 'team', label: 'Team', adminOnly: true }, { id: 'callcenter', label: 'Operatore call center', adminOnly: true },
  { id: 'fornitore_lead', label: 'Fornitore di lead' }, { id: 'gara', label: 'Gara' },
  { id: 'campagna', label: 'Campagna (solo costi)' }, { id: 'fornitore', label: 'Fornitore (solo costi)' },
]

function LevelsTab({ range, isAdmin }: { range: Range; isAdmin: boolean }) {
  const [dimension, setDimension] = useState<MarginDimension>('prodotto')
  const { data, loading, error } = useReport<MarginLevels>(
    () => financeReportsApi.marginLevels({ from: range.from, to: range.to, dimension }), [range.from, range.to, dimension], 'Impossibile caricare i margini per livello.',
  )
  const lv = data?.levels
  const rows = data?.rows ?? []
  return (
    <Section loading={loading} error={error} hasData={data !== null}>
      <Card variant="outlined">
        <CardHeader icon="rule" title="Come si calcolano i quattro margini" />
        <CardContent>
          <KeyValueList
            className="md-typescale-body-small"
            items={(data?.definitions ?? []).map(d => ({ label: d.label, value: <>{d.formula}</> }))}
          />
          <div className="text-muted md-typescale-body-small mt-2">
            I costi non attribuiti ai singoli margini di pratica si ripartiscono sulle righe in proporzione ai ricavi: la somma delle righe è sempre il totale.
            Il margine gestionale coincide con l’EBITDA del conto economico. Le imposte sono una stima ({data?.taxesLabel ?? 'STIMATO'}) letta da conti; senza conti il margine aziendale non si calcola.
          </div>
        </CardContent>
      </Card>
      {data?.restricted && <Callout tone="info">Gli importi non sono visibili con il tuo profilo.</Callout>}
      {lv && (
        <StatGrid>
          <StatCard label="Margine pratica" value={eur(lv.practice)} supporting="ricavo - provvigioni - lead" />
          <StatCard label="Margine commerciale" value={eur(lv.commercial)} supporting={`dopo ${eur(lv.unattributedCommercialCosts)} di marketing e call center`} />
          <StatCard label="Margine gestionale" value={eur(lv.management)} supporting={`dopo ${eur(lv.structureCosts)} di struttura`} />
          <StatCard label="Margine aziendale" value={lv.corporate === null ? '—' : eur(lv.corporate)} supporting={lv.estimatedTaxes === null ? 'imposte non disponibili da conti' : `imposte STIMATE ${eur(lv.estimatedTaxes)}`} />
        </StatGrid>
      )}
      <Select label="Per" value={dimension} onChange={e => setDimension(e.target.value as MarginDimension)}>
        {DIMENSIONS.filter(d => !d.adminOnly || isAdmin).map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
      </Select>
      {data?.costOnly ? (
        <DataTable aria-label="Costi per dimensione">
          <thead><tr><th>{dimension === 'campagna' ? 'Campagna' : 'Fornitore'}</th><th className="text-end">Costi</th></tr></thead>
          <tbody>
            {rows.length === 0 && <DataTableEmptyRow colSpan={2} title="Nessun dato" description="Nessuna spesa nel periodo." />}
            {rows.map(r => <tr key={String(r.key)}><td>{r.name}</td><td className="text-end md-num">{eur('cost' in r ? r.cost : undefined)}</td></tr>)}
          </tbody>
        </DataTable>
      ) : (
        <DataTable aria-label="Margini per livello">
          <thead>
            <tr>
              <th>{DIMENSIONS.find(d => d.id === dimension)?.label}</th><th className="text-end">Ricavi</th><th className="text-end">Provvigioni</th>
              <th className="text-end">Costo lead</th><th className="text-end">Quote setter e TL</th><th className="text-end">Pratica</th><th className="text-end">Commerciale</th>
              <th className="text-end">Gestionale</th><th className="text-end">Aziendale</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <DataTableEmptyRow colSpan={9} title="Nessun dato" description="Nessun ricavo maturato nel periodo." />}
            {(rows as MarginLevelsRow[]).map(r => (
              <tr key={String(r.key)}>
                <td>{r.drill ? <Link to={r.drill.href}>{r.name}</Link> : r.name}</td>
                <td className="text-end md-num">{eur(r.revenue)}</td>
                <td className="text-end md-num">{eur(r.commissions)}</td>
                <td className="text-end md-num">{eur(r.leadCost)}</td>
                <td className="text-end md-num">{eur(r.payoutQuotas ?? 0)}</td>
                <td className="text-end md-num">{eur(r.practice)}</td>
                <td className="text-end md-num">{eur(r.commercial)}</td>
                <td className="text-end md-num">{eur(r.management)}</td>
                <td className="text-end md-num">{r.corporate === null ? '—' : eur(r.corporate)}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
    </Section>
  )
}

/* ── Operatori ───────────────────────────────────────────────────────────────────────────────── */

function OperatorsTab({ range }: { range: Range }) {
  const { data, loading, error } = useReport<OperatorReportRow[]>(
    () => financeReportsApi.operators({ from: range.from, to: range.to }), [range.from, range.to], 'Impossibile caricare il report operatori.',
  )
  return (
    <Section loading={loading} error={error} hasData={data !== null}>
      <DataTable aria-label="Report per operatore">
        <thead>
          <tr>
            <th>Operatore</th><th className="text-end">Produzione</th><th className="text-end">Atteso</th><th className="text-end">Maturato</th>
            <th className="text-end">Incassato</th><th className="text-end">Residuo</th><th className="text-end">Ritardo medio</th>
            <th className="text-end">Storni</th><th className="text-end">Provvigioni</th><th className="text-end">Margine</th>
          </tr>
        </thead>
        <tbody>
          {(data ?? []).length === 0 && <DataTableEmptyRow colSpan={10} title="Nessun dato" description="Nessun operatore ha movimenti nel periodo." />}
          {(data ?? []).map(r => (
            <tr key={r.operatorId}>
              <td>{r.drill ? <Link to={r.drill.href}>{r.name}</Link> : r.name}</td>
              <td className="text-end md-num">{eur(r.production)}</td>
              <td className="text-end md-num">{eur(r.expected)}</td>
              <td className="text-end md-num">{eur(r.accrued)}</td>
              <td className="text-end md-num">{eur(r.collected)}</td>
              <td className="text-end md-num">{eur(r.residual)}</td>
              <td className="text-end md-num">{r.avgDelayDays === null ? '—' : `${num(r.avgDelayDays, 1)} gg`}</td>
              <td className="text-end md-num">{eur(r.reversals)}</td>
              <td className="text-end md-num">{eur(r.commissions)}</td>
              <td className="text-end md-num">{eur(r.margin)}</td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </Section>
  )
}

/* ── Pratiche ────────────────────────────────────────────────────────────────────────────────── */

function PracticesTab({ range, filters, departments, onClearFilters }: {
  range: Range; filters: PracticeFilters; departments: Department[]; onClearFilters: () => void
}) {
  const names = useOperatorNames()
  const [page, setPage] = useState(1)
  const pageSize = 25
  const filterKey = JSON.stringify(filters)
  useEffect(() => { setPage(1) }, [range.from, range.to, filterKey])
  const { data, loading, error } = useReport<PracticeMarginList>(
    () => financeReportsApi.practiceMargins({ from: range.from, to: range.to, page, pageSize, ...filters }),
    [range.from, range.to, page, filterKey], 'Impossibile caricare i margini per pratica.',
  )
  const active: string[] = []
  if (filters.operatorId) active.push(`operatore ${names.get(Number(filters.operatorId)) ?? filters.operatorId}`)
  if (filters.agentUserId) active.push(`agente n. ${filters.agentUserId}`)
  if (filters.departmentId) active.push(`settore ${departments.find(d => d.id === Number(filters.departmentId))?.name ?? filters.departmentId}`)
  if (filters.noDepartment) active.push('senza settore')
  if (filters.productId) active.push(`prodotto ${filters.productId}`)
  return (
    <Section loading={loading} error={error} hasData={data !== null}>
      {active.length > 0 && (
        <Callout tone="info">
          Pratiche filtrate per {active.join(', ')}.{' '}
          {data?.totals && <>Totale: ricavo {eur(data.totals.revenue)}, provvigioni {eur(data.totals.agentCommission)}, margine {eur(data.totals.margin)}. </>}
          <Button variant="text" size="sm" onClick={onClearFilters}>Togli il filtro</Button>
        </Callout>
      )}
      <DataTable aria-label="Margine per pratica" footer={<Pagination page={page} pageSize={pageSize} total={data?.total ?? 0} onChange={setPage} />}>
        <thead>
          <tr>
            <th>Pratica</th><th className="text-end">Ricavo</th><th className="text-end">Costo lead</th><th className="text-end">Setter</th>
            <th className="text-end">Provvigione agente</th><th className="text-end">Override</th><th className="text-end">Altri diretti</th><th className="text-end">Margine</th>
          </tr>
        </thead>
        <tbody>
          {(data?.items ?? []).length === 0 && <DataTableEmptyRow colSpan={8} title="Nessuna pratica" description="Nessuna pratica con ricavo nel periodo." />}
          {(data?.items ?? []).map(r => (
            <tr key={r.contractId}>
              <td><Link to={`/contracts/${r.contractId}`}>Pratica {r.contractId}</Link></td>
              <td className="text-end md-num">{eur(r.revenue)}</td>
              <td className="text-end md-num">{eur(r.leadCost)}</td>
              <td className="text-end md-num">{eur(r.setter)}</td>
              <td className="text-end md-num">{eur(r.agentCommission)}</td>
              <td className="text-end md-num">{eur(r.overrides)}</td>
              <td className="text-end md-num">{eur(r.otherDirect)}</td>
              <td className="text-end md-num">{eur(r.margin)}</td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </Section>
  )
}

/* ── Agenti ──────────────────────────────────────────────────────────────────────────────────── */

function AgentsTab({ range }: { range: Range }) {
  const names = useOperatorNames()
  const { data, loading, error } = useReport<AgentMarginRow[]>(
    () => financeReportsApi.agentsMargin({ from: range.from, to: range.to }), [range.from, range.to], 'Impossibile caricare il margine per agente.',
  )
  return (
    <Section loading={loading} error={error} hasData={data !== null}>
      <Callout tone="info">Il costo del call center non è configurato: dove manca compare «Non configurato» e non entra nel margine.</Callout>
      <DataTable aria-label="Margine per agente">
        <thead>
          <tr>
            <th>Agente</th><th className="text-end">Ricavo</th><th className="text-end">Provvigioni</th><th className="text-end">Costo lead</th>
            <th className="text-end">Costo call center</th><th className="text-end">Storni</th><th className="text-end">Margine</th>
          </tr>
        </thead>
        <tbody>
          {(data ?? []).length === 0 && <DataTableEmptyRow colSpan={7} title="Nessun dato" description="Nessun agente ha ricavi nel periodo." />}
          {(data ?? []).map(r => (
            <tr key={r.agentUserId}>
              <td>{r.drill ? <Link to={r.drill.href}>{r.name ?? names.get(r.agentUserId) ?? `Utente ${r.agentUserId}`}</Link> : (r.name ?? names.get(r.agentUserId) ?? `Utente ${r.agentUserId}`)}</td>
              <td className="text-end md-num">{eur(r.revenue)}</td>
              <td className="text-end md-num">{eur(r.commissions)}</td>
              <td className="text-end md-num">{eur(r.leadCost)}</td>
              <td className="text-end md-num">{r.callCenterCost === null ? 'Non configurato' : eur(r.callCenterCost)}</td>
              <td className="text-end md-num">{eur(r.reversals)}</td>
              <td className="text-end md-num">{eur(r.margin)}</td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </Section>
  )
}

/* ── Snapshot mensili ────────────────────────────────────────────────────────────────────────── */

const PAYLOAD_LABELS: Record<string, string> = {
  accrued: 'Maturato', collected: 'Incassato', costs: 'Costi', margin: 'Margine', receivables: 'Crediti', payables: 'Debiti',
  liquidity: 'Liquidità', taxes: 'Imposte', commissions: 'Provvigioni', partners: 'Saldi soci', marginPct: 'Margine %',
}

function payloadValue(key: string, v: unknown) {
  // Le imposte arrivano come { amount, label }: l'importo è una stima e lo resta (STIMATO).
  if (v && typeof v === 'object' && 'amount' in v) {
    const amount = (v as { amount: unknown }).amount
    return <>{typeof amount === 'number' ? eur(amount) : '—'} <StatusPill tone="warning">STIMATO</StatusPill></>
  }
  const label = /tax|impost/i.test(key) ? <> <StatusPill tone="warning">STIMATO</StatusPill></> : null
  if (typeof v === 'number') return <>{/pct|percent/i.test(key) ? pct(v) : eur(v)}{label}</>
  if (v === null || v === undefined) return <>—{label}</>
  if (typeof v === 'string') return <>{v}{label}</>
  return <>{JSON.stringify(v)}{label}</>
}

function SnapshotsTab() {
  const { data, loading, error } = useReport<SnapshotInfo[]>(() => financeReportsApi.snapshots(), [], 'Impossibile caricare gli snapshot.')
  const [open, setOpen] = useState<SnapshotInfo | null>(null)
  const [detail, setDetail] = useState<SnapshotDetail | null>(null)
  const [detailError, setDetailError] = useState('')

  useEffect(() => {
    if (!open) { setDetail(null); setDetailError(''); return }
    let alive = true
    void financeReportsApi.snapshot(open.year, open.month)
      .then(d => { if (alive) setDetail(d) })
      .catch(e => { if (alive) setDetailError(errMsg(e, 'Snapshot non disponibile.')) })
    return () => { alive = false }
  }, [open])

  return (
    <Section loading={loading} error={error} hasData={data !== null}>
      <Callout tone="info">Lo snapshot fotografa i numeri alla chiusura del mese e non cambia se le regole vengono modificate dopo. Una riapertura e nuova chiusura crea una nuova revisione.</Callout>
      <DataTable aria-label="Snapshot mensili">
        <thead><tr><th>Mese</th><th className="text-end">Revisione</th><th>Creato il</th><th><span className="md-sr-only">Azioni</span></th></tr></thead>
        <tbody>
          {(data ?? []).length === 0 && <DataTableEmptyRow colSpan={4} icon="photo_camera" title="Nessuno snapshot" description="Gli snapshot si creano alla chiusura contabile del mese." />}
          {(data ?? []).map(s => (
            <tr key={`${s.year}-${s.month}-${s.revision}`}>
              <td>{monthLabel(s.year, s.month)}</td>
              <td className="text-end md-num">{s.revision}</td>
              <td>{dateIt(s.createdAt)}</td>
              <td><Button variant="text" size="sm" icon="visibility" onClick={() => setOpen(s)}>Apri</Button></td>
            </tr>
          ))}
        </tbody>
      </DataTable>
      {open && (
        <Dialog
          open
          onClose={() => setOpen(null)}
          size="lg"
          title={`Snapshot di ${monthLabel(open.year, open.month)} (revisione ${open.revision})`}
          actions={<Button variant="text" dismiss onClick={() => setOpen(null)}>Chiudi</Button>}
        >
          {detailError && <Callout tone="error">{detailError}</Callout>}
          {!detail && !detailError && <LoadingState />}
          {detail && (() => {
            const entries = Object.entries(detail.payload ?? {}).filter(([k]) => k !== 'year' && k !== 'month')
            return entries.length === 0
              ? <EmptyState icon="inbox" title={detail.payload ? 'Snapshot vuoto' : 'Importi non visibili'} compact />
              : <KeyValueList columns={2} items={entries.map(([k, v]) => ({ label: PAYLOAD_LABELS[k] ?? k, value: payloadValue(k, v) }))} />
          })()}
        </Dialog>
      )}
    </Section>
  )
}
